import { AiOutlineBarcode, AiOutlineBgColors, AiOutlineExperiment, AiOutlineTag } from "react-icons/ai";
import { IconType } from "react-icons/lib";
import {
  MdAssignment,
  MdAttachMoney,
  MdCalendarMonth,
  MdChecklist,
  MdDescription,
  MdDirections,
  MdEditDocument,
  MdEventBusy,
  MdFactory,
  MdFolder,
  MdFolderCopy,
  MdFormatListBulleted,
  MdGroups,
  MdInbox,
  MdLink,
  MdListAlt,
  MdMap,
  MdMoreHoriz,
  MdOutlineChecklist,
  MdOutlineComputer,
  MdOutlineDiscount,
  MdOutlineFeed,
  MdOutlineHome,
  MdOutlineInventory2,
  MdOutlineMode,
  MdOutlineNotifications,
  MdOutlinePayments,
  MdOutlineProductionQuantityLimits,
  MdPallet,
  MdPerson,
  MdPersonAdd,
  MdPrecisionManufacturing,
  MdQrCodeScanner,
  MdRequestQuote,
  MdSchool,
  MdScience,
  MdSearch,
  MdSecurity,
  MdSettings,
  MdShoppingCart,
  MdSupport,
  MdSync,
  MdWallet,
  MdWaterDrop,
} from "react-icons/md";

import { AuthAttribute, pickRoleAttrs } from "@megaron/access-control";
import { CustomerCategory } from "@megaron/crm-contracts";
import { navBadgeTypes, SignalType, ticketViewerRoleAttributes } from "@megaron/docs-contracts";
import { HttpUri } from "@megaron/http-uri";
import { MegaraxUri } from "@megaron/megarax-uri";
import { AppId } from "@megaron/resource";

import { LoyaltyAdminLogo } from "./LoyaltyAdminLogo";

export const visibleCustomersTableColumnsKey = ["all", "loyalty", "pos", "payers", "distributors", "chains"] as const;

export type VisibleCustomersTableColumnsKey = (typeof visibleCustomersTableColumnsKey)[number];

export const getPosCustomersUrl = (category?: CustomerCategory) => {
  const params = {
    page: 0,
    searchText: "",
    isPro: false,
    isAffiliate: false,
    status: ["unverified", "verified", "vip"],
    categories: [category ?? "pos"],
  };

  const searchText = JSON.stringify(params);

  return `/customers/new-list?filters=${encodeURIComponent(searchText)}`;
};

export type LinkIcon =
  | IconType
  | React.FunctionComponent<
      React.SVGProps<SVGSVGElement> & {
        title?: string | undefined;
      }
    >;

export type AppLinkTree = {
  app: string;
  name: string;
  Icon: LinkIcon;
  color: string;
  uri: MegaraxUri | HttpUri;
  pageLinks: PageLink[];
  appLinkUrl?: string;
  hideAppLink?: boolean;
};

export type PageLink = {
  uri: MegaraxUri | HttpUri;
  name: string;
  authorizedAttributes: AuthAttribute[];
  Icon: LinkIcon;
  isActive?: (pathname: string, searchParams: URLSearchParams) => boolean;
  badgeSignalTypes?: readonly SignalType[];
};

const crm: AppLinkTree = {
  name: "CRM",
  app: "crm",
  color: "hsl(273, 75%, 25%)",
  uri: MegaraxUri("megarax://crm/home"),
  Icon: MdGroups,
  pageLinks: [
    {
      name: "Klienci",
      uri: MegaraxUri("megarax://crm/customers"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdPerson,
      isActive: (pathname) => {
        const isActive =
          pathname.startsWith("/crm/customers") &&
          !pathname.startsWith("/crm/customers/new/") &&
          pathname !== "/crm/customers/new";
        return !!isActive;
      },
    },
    {
      name: "Dodaj klienta",
      uri: MegaraxUri("megarax://crm/customers/new"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdPersonAdd,
      isActive: (pathname) => {
        return pathname === "/crm/customers/new" || pathname.startsWith("/crm/customers/new/");
      },
    },
    {
      name: "Regiony",
      uri: MegaraxUri("megarax://crm/regions"),
      authorizedAttributes: pickRoleAttrs("crm.*"),
      Icon: MdMap,
    },
    {
      name: "Strona główna",
      uri: MegaraxUri("megarax://crm/home"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdOutlineHome,
    },
    {
      name: "Moje zamówienia",
      uri: MegaraxUri("megarax://crm/order-drafts"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdShoppingCart,
    },
    {
      name: "Zamówienia",
      uri: MegaraxUri("megarax://crm/orders"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdPallet,
    },

    {
      name: "Moje wizyty",
      uri: MegaraxUri("megarax://crm/my-visits"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdCalendarMonth,
    },
    {
      name: "Trasy handlowe",
      uri: MegaraxUri("megarax://crm/trade-routes"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdDirections,
    },
    {
      name: "Moje ankiety cenowe",
      uri: MegaraxUri("megarax://crm/price-reports"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdAssignment,
    },
    {
      name: "Cenniki",
      uri: MegaraxUri("megarax://crm/price-lists"),
      authorizedAttributes: pickRoleAttrs("catalog.*"),
      Icon: MdAttachMoney,
    },
    {
      name: "Sync warunków handlowych",
      uri: MegaraxUri("megarax://crm/diff-sync/{channel}", { channel: "sheetsPricingRule" }),
      authorizedAttributes: pickRoleAttrs("crm.admin"),
      Icon: MdSync,
    },
    {
      name: "Migracja warunków handlowych",
      uri: MegaraxUri("megarax://crm/diff-sync/{channel}", { channel: "legacyMegaraxPricingRule" }),
      authorizedAttributes: pickRoleAttrs("crm.admin"),
      Icon: MdSync,
    },
    {
      name: "Ankiety cenowe",
      uri: MegaraxUri("megarax://crm/price-report-products"),
      authorizedAttributes: pickRoleAttrs("crm.admin"),
      Icon: MdOutlineChecklist,
    },

    {
      name: "Rabaty posprzedażowe",
      uri: MegaraxUri("megarax://crm/post-sale-discounts"),
      authorizedAttributes: pickRoleAttrs("crm.admin"),
      Icon: MdOutlineDiscount,
    },
    {
      name: "Plan marży",
      uri: MegaraxUri("megarax://crm/profit-targets"),
      authorizedAttributes: pickRoleAttrs("crm.admin"),
      Icon: MdFormatListBulleted,
    },
    {
      name: "Moje zaproszenie",
      uri: MegaraxUri("megarax://crm/my-affiliate-codes"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdPersonAdd,
    },
    {
      name: "Dostępność produktów",
      uri: MegaraxUri("megarax://crm/product-availability"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdOutlineProductionQuantityLimits,
    },
    {
      name: "Szkolenia",
      uri: MegaraxUri("megarax://crm/training-calendar"),
      authorizedAttributes: pickRoleAttrs("crm.admin", "crm.supervisor", "crm.user"),
      Icon: MdSchool,
    },
    {
      name: "Zgłoszenia",
      uri: MegaraxUri("megarax://support/tickets"),
      authorizedAttributes: ticketViewerRoleAttributes,
      Icon: MdSupport,
      badgeSignalTypes: navBadgeTypes("unreadTicket"),
    },
  ],
};

const catalog: AppLinkTree = {
  name: "Oferta",
  app: "catalog",
  color: "hsl(150, 65%, 28%)",
  uri: MegaraxUri("megarax://catalog/items"),
  Icon: MdOutlineInventory2,
  pageLinks: [
    {
      name: "Produkty",
      uri: MegaraxUri("megarax://catalog/items"),
      authorizedAttributes: pickRoleAttrs("catalog.*"),
      Icon: AiOutlineBarcode,
    },
    {
      name: "Cenniki",
      uri: MegaraxUri("megarax://catalog/price-lists"),
      authorizedAttributes: pickRoleAttrs("catalog.*"),
      Icon: MdAttachMoney,
    },
  ],
};

const loyaltyAdmin: AppLinkTree = {
  name: "Specjalista",
  app: "loyaltyAdmin",
  color: "hsl(328, 75%, 25%)",
  uri: MegaraxUri("megarax://loyalty/lines"),
  Icon: LoyaltyAdminLogo,
  pageLinks: [
    {
      name: "Linie produkcyjne",
      uri: MegaraxUri("megarax://loyalty/lines"),
      authorizedAttributes: pickRoleAttrs("loyalty-qr-sync.*"),
      Icon: MdPrecisionManufacturing,
    },
    {
      name: "Pokrywki",
      uri: MegaraxUri("megarax://loyalty/batches"),
      authorizedAttributes: pickRoleAttrs("loyalty-points.admin", "loyalty-points.code-group-user"),
      Icon: MdQrCodeScanner,
    },
    {
      name: "Grupy kodów",
      uri: MegaraxUri("megarax://loyalty/code-groups"),
      authorizedAttributes: pickRoleAttrs("loyalty-points.*"),
      Icon: MdChecklist,
    },
    {
      name: "Czeki BLIK",
      uri: MegaraxUri("megarax://loyalty/blik-checks"),
      authorizedAttributes: pickRoleAttrs("loyalty-points.*", "fulfillment.user"),
      Icon: MdOutlinePayments,
    },
    {
      name: "Konta użytkowników",
      uri: MegaraxUri("megarax://loyalty/accounts"),
      authorizedAttributes: pickRoleAttrs("loyalty-points.*", "fulfillment.user"),
      Icon: MdPerson,
    },
    {
      name: "Sprawdź kod",
      uri: MegaraxUri("megarax://loyalty/inspect-code"),
      authorizedAttributes: pickRoleAttrs("loyalty-points.*", "crm.*"),
      Icon: MdSearch,
    },
    {
      name: "Koszyki",
      uri: MegaraxUri("megarax://loyalty/carts"),
      authorizedAttributes: pickRoleAttrs("fulfillment.admin", "fulfillment.user"),
      Icon: MdShoppingCart,
    },
    {
      name: "Katalog nagród",
      uri: MegaraxUri("megarax://loyalty/listings"),
      authorizedAttributes: pickRoleAttrs("loyalty-points.admin", "loyalty-points.viewer"),
      Icon: MdListAlt,
    },
    {
      name: "Realizacja zamówień",
      uri: MegaraxUri("megarax://loyalty/order-fulfillment"),
      authorizedAttributes: pickRoleAttrs("fulfillment.user", "fulfillment.admin", "fulfillment.packing-user"),
      Icon: MdOutlineInventory2,
      badgeSignalTypes: navBadgeTypes("loyaltyAdminOrderCreated"),
    },
    {
      name: "Przewodnik Specjalista",
      uri: "https://docs.google.com/document/d/1RJuf_TdU40TakW3u8Iok_J_1N6tPYvPcrushX1SWm3E",
      authorizedAttributes: ["userType:megarax"],
      Icon: MdOutlineFeed,
    },
  ],
};

const invoices: AppLinkTree = {
  name: "Faktury",
  uri: MegaraxUri("megarax://invoices/threads"),
  app: "invoices",
  color: "hsl(234, 75%, 25%)",
  Icon: MdRequestQuote,
  pageLinks: [
    {
      name: "Wersje robocze",
      uri: MegaraxUri("megarax://invoices/drafts"),
      authorizedAttributes: pickRoleAttrs("invoices.*"),
      Icon: MdEditDocument,
      badgeSignalTypes: navBadgeTypes("draftInvoice"),
    },
    {
      name: "Moje faktury",
      uri: MegaraxUri("megarax://invoices/threads", {
        filters: {
          sort: { field: "invitedAt", order: "ASC" },
          status: ["open"],
          groupFilter: "mine",
          isMine: "true",
        },
      }),
      authorizedAttributes: pickRoleAttrs("invoices.coordinator", "invoices.admin"),
      Icon: MdInbox,
      badgeSignalTypes: navBadgeTypes("unreadInvoice"),
      isActive: (pathname, searchParams) => {
        const isActive =
          pathname.startsWith("/faktury/watki") && searchParams.get("filters")?.includes(`"isMine":"true"`);
        return !!isActive;
      },
    },
    {
      name: "Faktury",
      uri: MegaraxUri("megarax://invoices/threads"),
      authorizedAttributes: pickRoleAttrs("invoices.*"),
      Icon: MdFolder,
      isActive: (pathname, searchParams) => {
        const isActive =
          pathname.startsWith("/faktury/watki") && !searchParams.get("filters")?.includes(`"isMine":"true"`);
        return !!isActive;
      },
    },
    {
      name: "Budżety",
      uri: MegaraxUri("megarax://invoices/budgets"),
      authorizedAttributes: pickRoleAttrs("invoices.coordinator", "invoices.admin"),
      Icon: MdWallet,
    },
  ],
};

const support: AppLinkTree = {
  name: "Support",
  uri: MegaraxUri("megarax://support/tickets"),
  app: "support",
  color: "hsl(200, 65%, 35%)",
  Icon: MdSupport,
  pageLinks: [
    {
      name: "Zgłoszenia",
      uri: MegaraxUri("megarax://support/tickets"),
      authorizedAttributes: ticketViewerRoleAttributes,
      Icon: MdAssignment,
    },
    {
      name: "Ustawienia",
      uri: MegaraxUri("megarax://support/settings"),
      authorizedAttributes: pickRoleAttrs("support.admin", "crm.admin"),
      Icon: MdSettings,
    },
  ],
};

const iam: AppLinkTree = {
  name: "IAM",
  uri: MegaraxUri("megarax://iam/users"),
  app: "iam",
  color: "hsl(191, 75%, 25%)",
  Icon: MdSecurity,
  pageLinks: [
    {
      name: "Użytkownicy",
      uri: MegaraxUri("megarax://iam/users"),
      authorizedAttributes: pickRoleAttrs("iam.admin", "iam.users-admin"),
      Icon: MdPerson,
    },
    {
      name: "Stanowiska",
      uri: MegaraxUri("megarax://iam/groups"),
      authorizedAttributes: pickRoleAttrs("iam.admin", "iam.users-admin"),
      Icon: MdGroups,
    },
  ],
};

const plm: AppLinkTree = {
  name: "PLM",
  app: "plm",
  uri: "https://plm.megarax.net/home",
  color: "hsl(14, 75%, 25%)",
  Icon: MdScience,
  pageLinks: [
    {
      name: "Decyzje",
      uri: "https://plm.megarax.net/home",
      authorizedAttributes: pickRoleAttrs("plm.*"),
      Icon: MdDescription,
    },
    {
      name: "Komponenty",
      uri: "https://plm.megarax.net/plm/komponent",
      authorizedAttributes: pickRoleAttrs("plm.user(komponent)", "plm.manager"),
      Icon: MdWaterDrop,
    },
    {
      name: "Receptury",
      uri: "https://plm.megarax.net/plm/receptura",
      authorizedAttributes: pickRoleAttrs("plm.user(receptura)", "plm.manager"),
      Icon: MdOutlineMode,
    },
    {
      name: "Produkty",
      uri: "https://plm.megarax.net/plm/produkt",
      authorizedAttributes: pickRoleAttrs("plm.user(produkt)", "plm.manager"),
      Icon: AiOutlineExperiment,
    },
    {
      name: "Wyroby",
      uri: "https://plm.megarax.net/plm/wyroby",
      authorizedAttributes: pickRoleAttrs("plm.user(wyrob)", "plm.manager"),
      Icon: AiOutlineBgColors,
    },
    {
      name: "Asortymenty",
      uri: "https://plm.megarax.net/plm/asortyment",
      authorizedAttributes: pickRoleAttrs("plm.user(asortyment)", "plm.manager"),
      Icon: AiOutlineTag,
    },
    {
      name: "Receptury produkcyjne",
      uri: "https://plm.megarax.net/plm/recepturaProdukcyjna",
      authorizedAttributes: pickRoleAttrs("plm.user(productionRecipe)", "plm.manager"),
      Icon: MdFactory,
    },
  ],
};

const dev: AppLinkTree = {
  name: "DEV",
  app: "dev",
  uri: MegaraxUri("megarax://dev/environment"),
  color: "hsl(205, 75%, 25%)",
  Icon: MdOutlineComputer,
  pageLinks: [
    {
      name: "Environment",
      uri: MegaraxUri("megarax://dev/environment"),
      authorizedAttributes: pickRoleAttrs("dev.admin"),
      Icon: MdSettings,
    },
    {
      name: "Notifications",
      uri: MegaraxUri("megarax://dev/notifications"),
      authorizedAttributes: pickRoleAttrs("dev.admin"),
      Icon: MdOutlineNotifications,
    },
    {
      name: "Docs",
      uri: MegaraxUri("megarax://dev/docs"),
      authorizedAttributes: pickRoleAttrs("dev.admin"),
      Icon: MdFolderCopy,
    },
  ],
};

const external: AppLinkTree = {
  name: "Pozostałe",
  app: "external",
  uri: "megarax://home",
  color: "#333",
  Icon: MdMoreHoriz,
  hideAppLink: true,
  pageLinks: [
    {
      name: "Plan urlopów",
      uri: "https://docs.google.com/spreadsheets/d/1EpJe20YvyVLWPtGXVUdSwhpVj64_XoniUdkTJ-vmkMA",
      authorizedAttributes: ["userType:megarax"],
      Icon: MdEventBusy,
    },
    {
      name: "Szkolenia",
      uri: "https://sites.google.com/megaron.com.pl/szkolenia/",
      authorizedAttributes: ["userType:megarax"],
      Icon: MdSchool,
    },
    {
      name: "Shoper",
      uri: "https://sklep712495.shoparena.pl/admin/",
      authorizedAttributes: ["userType:megarax"],
      Icon: MdLink,
    },
    {
      name: "Way2Send (W2S)",
      uri: "https://w2s.way2send.pl/login.aspx?ReturnUrl=%2fdefault.aspx",
      authorizedAttributes: ["userType:megarax"],
      Icon: MdLink,
    },
    {
      name: "Kibana",
      uri: "http://kibana.megarax.net",
      authorizedAttributes: ["userType:megarax"],
      Icon: MdLink,
    },
    {
      name: "Business Central (BC)",
      uri: "https://businesscentral.dynamics.com/Megaron",
      authorizedAttributes: ["userType:megarax"],
      Icon: MdLink,
    },
  ],
};

export const appLinkTrees = [invoices, crm, catalog, loyaltyAdmin, plm, iam, external, dev];
export const appLinkTreesObject: Record<AppId, AppLinkTree> & Record<string, AppLinkTree> = {
  invoices,
  crm,
  catalog,
  loyaltyAdmin,
  support,
  plm,
  iam,
  external,
  dev,
};
